import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import { Eye, Star } from "lucide-react";
import { Button } from "@/components/ui/button";
import CheckoutButton from "@/components/CheckoutButton";

const formatCategory = (category = "") =>
  category
    .split("-")
    .map((word) => word.charAt(0).toUpperCase() + word.slice(1))
    .join(" ");

const ProductCard = ({ product, index = 0, wide = false }) => (
  <motion.article
    initial={{ opacity: 0, y: 30 }}
    animate={{ opacity: 1, y: 0 }}
    transition={{ delay: index * 0.1 }}
    className={`group relative overflow-hidden rounded-xl border border-slate-800 bg-slate-900/40 backdrop-blur-md transition-all duration-300 hover:border-primary/50 card-hover ${
      wide ? "md:grid md:grid-cols-[minmax(0,1.25fr)_minmax(300px,0.75fr)]" : ""
    }`}
    data-testid={`product-card-${product.id}`}
  >
    <div
      className={`relative overflow-hidden ${
        wide
          ? "h-64 rounded-t-xl md:h-full md:min-h-[340px] md:rounded-l-xl md:rounded-tr-none"
          : "h-48 rounded-t-xl"
      }`}
    >
      <img
        src={product.image_url || "/logo-icon.svg"}
        alt={product.name}
        loading="lazy"
        className="h-full w-full object-cover transition-transform duration-500 group-hover:scale-105"
      />
      <div className="product-image-overlay rounded-t-xl" />
      {product.is_featured && (
        <div className="absolute right-3 top-3 flex items-center gap-1 rounded-full bg-primary/90 px-3 py-1.5 text-[10px] font-bold uppercase tracking-wider text-white">
          <Star className="h-3 w-3" /> Featured
        </div>
      )}
      <div className="absolute bottom-4 left-4 right-4 hidden gap-2 transition-all duration-300 md:flex md:translate-y-4 md:opacity-0 md:group-hover:translate-y-0 md:group-hover:opacity-100">
        <Link to={`/product/${product.id}`} className="flex-1">
          <Button
            variant="secondary"
            data-testid={`product-detail-btn-${product.id}`}
            className="w-full rounded-lg border border-slate-700 bg-slate-950/80 text-xs uppercase tracking-wider text-foreground hover:border-primary/50"
          >
            <Eye className="mr-2 h-4 w-4" /> Details
          </Button>
        </Link>
        <CheckoutButton
          product={product}
          compact
          className="flex-1 rounded-lg bg-primary/90 text-xs uppercase tracking-wider text-primary-foreground hover:bg-primary"
        />
      </div>
    </div>

    <div className={`p-5 ${wide ? "flex flex-col justify-center md:p-8" : ""}`}>
      {product.category && (
        <span className="mb-2 block text-[10px] uppercase tracking-widest text-primary">
          {formatCategory(product.category)}
        </span>
      )}
      <h2 className={`mb-2 font-bold tracking-wide text-foreground transition-colors group-hover:text-primary ${wide ? "text-2xl" : "text-lg"}`}>
        {product.name}
      </h2>
      <p className={`mb-4 text-sm leading-6 text-muted-foreground ${wide ? "line-clamp-4" : "line-clamp-2"}`}>
        {product.short_description}
      </p>
      <div className="flex items-center justify-between">
        <span className="price-tag text-xl">${Number(product.price).toFixed(2)}</span>
        <Link to={`/product/${product.id}`} className="text-xs uppercase tracking-widest text-primary hover:text-accent">
          Learn more →
        </Link>
      </div>
      <CheckoutButton
        product={product}
        compact
        className="mt-4 w-full rounded-lg bg-primary/90 text-xs uppercase tracking-wider text-primary-foreground"
      />
    </div>
    <div className="absolute bottom-0 left-0 right-0 h-[2px] bg-gradient-to-r from-transparent via-primary/50 to-transparent opacity-0 transition-opacity group-hover:opacity-100" />
  </motion.article>
);

export default ProductCard;
